import { AlternateLinkComponent_Plain } from "../types/components/page/AlternateLinkComponent";

export type AlternateLink = Pick<
  AlternateLinkComponent_Plain,
  "locale" | "url"
>;

export const LOCALE_TO_PREFIX: Record<string, string> = {
  en: "",
  uk: "/ua",
  ro: "/ro",
  pl: "/pl",
  hr: "/hr",
  el: "/el",
  bg: "/bg",
  lt: "/lt",
  it: "/it",
  hu: "/hu",
};

export const HEADER_LOCALE_MAP: Record<string, string> = {
  ENGLISH: "en",
  ROMANIAN: "ro",
  POLISH: "pl",
  CROATIAN: "hr",
  GREEK: "el",
  BULGARIAN: "bg",
  UKRAINIAN: "uk",
  LITHUANIAN: "lt",
  ITALIAN: "it",
  HUNGARIAN: "hu",
};

export const LOCALE_TO_HREFLANG: Record<string, string> = {
  en: "en",
  uk: "uk",
  ro: "ro",
  pl: "pl",
  hr: "hr",
  el: "el",
  bg: "bg",
  lt: "lt",
  it: "it",
  hu: "hu",
};

export function normalizePath(url: string): string {
  if (!url) return "/";
  const path = url.startsWith("http") ? new URL(url).pathname : url;
  if (path === "/") return path;
  return path.replace(/\/+$/, "") || "/";
}

export function buildHreflangTags(
  alternateLinks: AlternateLink[] | undefined,
  baseUrl: string,
): string {
  if (!alternateLinks?.length) {
    return "";
  }

  const tags = alternateLinks.map(({ locale, url }) => {
    const hreflang = LOCALE_TO_HREFLANG[locale] ?? locale;
    return `<link rel="alternate" hreflang="${hreflang}" href="${baseUrl}${normalizePath(url)}" />`;
  });

  const enLink = alternateLinks.find((link) => link.locale === "en");
  if (enLink) {
    tags.push(
      `<link rel="alternate" hreflang="x-default" href="${baseUrl}${normalizePath(enLink.url)}" />`,
    );
  }

  return tags.join("\n    ");
}

export function getSwitchUrl(
  currentPath: string,
  targetLocale: string,
  alternateLinks?: AlternateLink[],
): string {
  const expectedPrefix = LOCALE_TO_PREFIX[targetLocale] ?? "";

  const mapped = alternateLinks?.find((link) => link.locale === targetLocale);
  if (mapped?.url) {
    const url = normalizePath(mapped.url);
    // Validate: the URL from alternateLinks must match the expected locale prefix.
    // English (prefix "") should NOT start with any other locale prefix.
    // Other locales should start with their own prefix.
    if (expectedPrefix === "") {
      const allPrefixes = Object.values(LOCALE_TO_PREFIX).filter(
        (p) => p !== "",
      );
      if (!allPrefixes.some((p) => url.startsWith(p + "/") || url === p)) {
        return url;
      }
      // URL has a wrong locale prefix for English – fall through to prefix-based
    } else {
      if (url.startsWith(expectedPrefix + "/") || url === expectedPrefix) {
        return url;
      }
      // URL doesn't match the expected locale prefix – fall through to prefix-based
    }
  }

  const result = expectedPrefix + (currentPath === "/" ? "" : currentPath);
  return result || "/";
}

export function extractPageRecordFromResponse(
  pageData: unknown,
): unknown | undefined {
  if (!pageData || typeof pageData !== "object") {
    return undefined;
  }

  const record = pageData as { data?: unknown };
  if (Array.isArray(record.data)) {
    return record.data[0];
  }

  return record.data;
}

export function extractAlternateLinksFromPageData(
  data: unknown,
): AlternateLink[] | undefined {
  if (!data || typeof data !== "object") {
    return undefined;
  }

  const record = data as {
    alternateLinks?: AlternateLink[] | null;
  };

  if (!record.alternateLinks?.length) {
    return undefined;
  }

  return record.alternateLinks
    .filter((link) => link?.locale && link?.url)
    .map(({ locale, url }) => ({ locale, url: normalizePath(url) }));
}

export function isLocaleShownInSwitcher(
  localeCode: string,
  alternateLinks?: AlternateLink[],
  currentLocale?: string,
): boolean {
  if (!alternateLinks?.length) {
    return !!currentLocale && localeCode === currentLocale;
  }

  if (currentLocale && localeCode === currentLocale) {
    return true;
  }

  return alternateLinks.some((link) => link.locale === localeCode);
}
